
import { Component, Vue, Prop } from "vue-property-decorator";
import { Mutation, namespace } from 'vuex-class';
import { Group } from '@/store/eventqueue/types';
import { VueBootstrapField } from '@/store/types';
import { 
    LockdownConfiguration,
    UpdateLockdownDetailsPayload,
    StaleConfigurationData,
    LockdownConfigurationByDesignationNewCFL, 
    UpdatedLockdownClearanceByName,
    LockdownConfigurationByDesignation
} from '@/store/lockdown/types';
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { AreaNode } from '@/types/groups/AreaNode';

const Lockdown = namespace("lockdown");

@Component({
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
	},
})
export default class StaleDataModal extends Vue {
    @Prop({ type: Object }) private selectedGroup: AreaNode;
    
    private fields: VueBootstrapField[] = [
		{ key: "isDesignated", label: "Designated", sortable: true, class: "text-center" },
		{ key: "serverTitle", label: "Server", sortable: true },
		{
			key: "clearanceFilterLevel",
			label: "Old Clearance Filter Level",
			sortable: true,
			class: "text-center"
		},
        {
			key: "newClearanceLevel",
			label: "New Clearance Filter Level",
			sortable: true,
			class: "text-center"
		},
	];
            
    @Mutation private setIsLoading: (loading: boolean) => void;

	@Lockdown.Action private updateClearanceFilterLevel: (updatedLockdownDetailsByName: UpdatedLockdownClearanceByName[]) => Promise<boolean>;
    @Lockdown.State private staleData: StaleConfigurationData[];
    @Lockdown.State private staleDataModalVisible: boolean;
    @Lockdown.State private selectedClearanceLevel: number;
    @Lockdown.State private lockdownConfigurationPayload: UpdateLockdownDetailsPayload;
    @Lockdown.State(state => state.lockdownConfigurations) private lockdownConfigurations: LockdownConfigurationByDesignation[];
    @Lockdown.Mutation private setStaleDataModalVisible:(visibleFlag: boolean) => void;
    @Lockdown.Mutation private setLockdownConfiguration:(configuration: LockdownConfiguration) => void;
	@Lockdown.Mutation private setUpdateReasonOnLockdownConfiguration:(reason: string) => void;

    private get staleDataConfigurations(): LockdownConfigurationByDesignationNewCFL[] {
        return this.staleData.map((sd: StaleConfigurationData) => {
            return {
                groupId: sd.lockdownConfiguration.groupId,
                isDesignated: sd.lockdownConfiguration.isDesignated,
                serverTitle: sd.lockdownConfiguration.serverTitle,
                clearanceFilterLevel: sd.lockdownConfiguration.clearanceFilterLevel,
                availableClearanceLevels: sd.lockdownConfiguration.availableClearanceLevels,
                cCureClearanceStates: sd.lockdownConfiguration.cCureClearanceStates,
                newClearanceLevel: sd.newClearanceLevel,
                name: sd.lockdownConfiguration.name,
                selected: sd.lockdownConfiguration.selected
            }
        });
    }

    public async handleSaveClick(): Promise<void> {
        this.setStaleDataModalVisible(false);

        this.setIsLoading(true);

        this.setUpdateReasonOnLockdownConfiguration(this.lockdownConfigurationPayload.lockdownConfiguration.updateReason);

        try {
            const result = await this.updateClearanceFilterLevel(this.lockdownConfigurationPayload.updatedLockdownClearanceLevels);

			if(result) {
				this.$notify({
					type: "success",
					title: "Clearance Filter Level Updated",
					text: "Success"
				});
			}
        } catch {
            this.$notify({
				type: "error",
				title: "Error",
				text: "Unable to update the clearance filter levels"
			});
        } finally {
            this.setIsLoading(false);
            this.$emit("clear-selected-clearance-level");
        }
        
    }

    private badgeClass(item): string {
		switch (item) {
			case 1:
			case 2:
				return "success";
			case 3:
			case 4:
				return "warning";
			case 5:
			case 6:
				return "danger";
		}
	}

    public handleModalHidden(): void {
        this.setStaleDataModalVisible(false);
    }

    private get clearanceLevelToUpdateTo(): number {
        return this.lockdownConfigurationPayload.updatedLockdownClearanceLevels[0].level;
    }

    private get staleDataMessage(): string {
        let message: string =`If you would like to continue and update the clearance filter level to ${this.clearanceLevelToUpdateTo}`;
        
        const numServersToUpdate = this.lockdownConfigurationPayload.updatedLockdownClearanceLevels.length;

        if (this.lockdownConfigurations.length === numServersToUpdate) {
            message += ` for all servers for ${this.selectedGroup?.label}`;
        } else if(numServersToUpdate > 1) {
            message += ` for ${numServersToUpdate} servers for ${this.selectedGroup?.label}`;
        } else {
            const groupNameOfServer = this.lockdownConfigurationPayload.updatedLockdownClearanceLevels[0].name;
        
            const serverToUpdateTitle = this.lockdownConfigurations.find((config) => config.name === groupNameOfServer);

            message += ` for ${serverToUpdateTitle?.serverTitle}`;
        }

        message += ", press 'Save'.";

        return message;
    }
}

